/**
 * Componente YearRow para cenários AVCB e Alvarás
 * Replica o layout do backup: year-label + boxes verde/amarelo/vermelho/preto
 */
import { cn } from '@/lib/utils';
import type { CenarioResponse } from '../hooks';

interface YearRowProps {
  year: number;
  month?: number; // Para identificar o mês do snapshot (ex: "Dez 2025")
  isForecast?: boolean; // Para linhas de forecast
  referenceLabel?: string; // Rótulo visual de referência (ex: "Meta: 1.820"), não altera totais
  data: CenarioResponse;
  isLoading?: boolean;
  className?: string;
}

export function YearRow({ year, month, isForecast, referenceLabel, data, isLoading, className }: YearRowProps) {
  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-4 p-4 bg-muted/30 rounded-lg animate-pulse', className)}>
        <div className="w-20 h-12 bg-muted rounded" />
        <div className="flex-1 flex items-center gap-2">
          <div className="w-24 h-16 bg-muted rounded" />
          <div className="w-24 h-16 bg-muted rounded" />
          <div className="w-24 h-16 bg-muted rounded" />
          <div className="w-24 h-16 bg-muted rounded" />
        </div>
      </div>
    );
  }

  const monthNames = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  
  // Determina o label do ano
  let yearLabel: string;
  if (isForecast) {
    yearLabel = `${year} Forecast`;
  } else if (month) {
    yearLabel = `${monthNames[month]} ${year}`;
  } else {
    yearLabel = String(year);
  }

  return (
    <div 
      className={cn(
        'flex items-center gap-4 p-4 bg-card rounded-lg border',
        isForecast && 'border-dashed border-blue-400/50 bg-blue-50/5',
        className
      )}
      data-pdf-block
    >
      {/* Year Label */}
      <div className="w-28 text-center flex flex-col">
        <span className={cn(
          'text-2xl font-bold text-foreground',
          isForecast && 'text-blue-500'
        )}>
          {year}
        </span>
        {isForecast && (
          <span className="text-xs text-blue-500 font-medium">Forecast</span>
        )}
        {isForecast && referenceLabel && (
          <span className="mt-1 inline-block rounded-full border border-blue-400/60 px-2 py-0.5 text-[10px] font-semibold text-blue-500">
            {referenceLabel}
          </span>
        )}
        {month && !isForecast && (
          <span className="text-xs text-muted-foreground">{monthNames[month]}</span>
        )}
      </div>

      {/* Boxes */}
      <div className="flex-1 flex items-center gap-3 flex-wrap">
        {/* Regular (Verde) */}
        <div className="flex items-center gap-2">
          <Box color="green" value={data.regular} percent={data.regularPercent} label="Regular" showPercent />
          <span className="text-xl font-bold text-muted-foreground">+</span>
        </div>

        {/* Em Andamento (Amarelo) */}
        <div className="flex items-center gap-2">
          <Box color="yellow" value={data.andamento} percent={data.andamentoPercent} label="Em Andamento" showPercent />
          <span className="text-xl font-bold text-muted-foreground">+</span>
        </div>

        {/* Pendente (Vermelho) */}
        <div className="flex items-center gap-2">
          <Box color="red" value={data.pendente} percent={data.pendentePercent} label="Pendente" showPercent />
          <span className="text-lg text-muted-foreground">=</span>
        </div>

        {/* Total (Preto/Cinza) */}
        <Box color="black" value={data.total} percent={100} label="Total" showPercent />
      </div>
    </div>
  );
}

// ============================================================================
// BOX COMPONENT
// ============================================================================

interface BoxProps {
  color: 'green' | 'yellow' | 'red' | 'blue' | 'black';
  value: number;
  percent: number;
  label: string;
  showPercent?: boolean;
}

function Box({ color, value, percent, label, showPercent }: BoxProps) {
  const colorClasses = {
    green: 'bg-emerald-500 dark:bg-emerald-600 text-white',
    yellow: 'bg-amber-500 dark:bg-amber-600 text-white',
    red: 'bg-red-500 dark:bg-red-600 text-white',
    blue: 'bg-blue-900 dark:bg-blue-800 text-white',
    black: 'bg-gray-800 dark:bg-gray-700 text-white',
  };

  const percentBarColors = {
    green: 'bg-emerald-300/50',
    yellow: 'bg-amber-300/50',
    red: 'bg-red-300/50',
    blue: 'bg-blue-300/50',
    black: 'bg-gray-400/50',
  };

  return (
    <div className="flex flex-col items-center gap-1">
      {/* Box principal */}
      <div className={cn('relative min-w-[80px] px-3 py-2 rounded-md overflow-hidden', colorClasses[color])}>
        {/* Barra de percentual */}
        <div 
          className={cn('absolute inset-y-0 left-0', percentBarColors[color])}
          style={{ width: `${Math.min(100, percent)}%` }}
        />
        
        {/* Conteúdo */}
        <div className="relative z-10 text-center">
          <div className="text-lg font-bold">{value.toLocaleString('pt-BR')}</div>
          {showPercent && (
            <div className="text-xs opacity-80">{percent.toFixed(1)}%</div>
          )}
        </div>
      </div>
      
      {/* Label */}
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

// ============================================================================
// RISCOS BOX (sidebar dos cenários)
// ============================================================================

interface RiscosBoxProps {
  type: 'avcb' | 'alvara';
  className?: string;
}

export function RiscosBox({ type, className }: RiscosBoxProps) {
  const riscos = type === 'avcb' 
    ? [
        'Multas por falta de AVCB',
        'Interdição do imóvel',
        'Responsabilidade civil/criminal',
        'Problemas com seguro',
      ]
    : [
        'Multas por funcionamento irregular',
        'Interdição do estabelecimento',
        'Responsabilidade administrativa',
        'Impedimento de renovação de contratos',
      ];

  return (
    <div className={cn('bg-gray-900 dark:bg-gray-950 text-white p-4 rounded-lg', className)}>
      <h4 className="text-sm font-semibold mb-3 text-red-400">⚠️ Riscos de Não Conformidade</h4>
      <ul className="space-y-2">
        {riscos.map((risco, i) => (
          <li key={i} className="text-xs text-gray-300 flex items-start gap-2">
            <span className="text-red-400">•</span>
            {risco}
          </li>
        ))}
      </ul>
    </div>
  );
}
